<template>
  <div class="account-mapping-container">
    <div class="content-wrapper">
      <!-- 左边：账户列表 -->
      <div class="account-section">
        <div class="section-header">
          <h3>账户列表</h3>
        </div>
        <div class="account-list">
          <div
            v-for="account in accountList"
            :key="account.uuid"
            :class="['account-item', { 'active': selectedAccountId === account.uuid }]"
            @click="handleSelectAccount(account)"
          >
            <div class="account-info">
              <div class="account-name">{{ account.name }}</div>
              <div class="account-detail">账号: {{ account.username }}</div>
              <div class="account-detail">昵称: {{ account.alias_name }}</div>
              <div class="account-detail">角色: {{ account.role_name || '未分配' }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右边：门店勾选 -->
      <div class="store-section">
        <div class="section-header">
          <h3>分配门店</h3>
          <div v-if="selectedAccountId">
            <el-button type="primary" size="small" @click="handleSelectAll">全选</el-button>
            <el-button size="small" @click="handleCancelAll">取消全选</el-button>
            <el-button type="success" size="small" @click="handleSaveMapping">保存分配</el-button>
          </div>
        </div>
        <div v-if="!selectedAccountId" class="empty-tip">
          <i class="el-icon-info"></i>
          <p>请先选择左侧账户</p>
        </div>
        <div v-else class="store-list">
          <el-checkbox-group v-model="selectedStoreIds">
            <div v-for="store in storeList" :key="store.store_id" class="store-item">
              <el-checkbox :label="store.store_id">
                <div class="store-content">
                  <div class="store-name">{{ store.store_name }}</div>
                  <div class="store-info">
                    <span>店长姓名: {{ store.boss_name }}</span>
                    <span>电话: {{ store.store_phone }}</span>
                  </div>
                  <div class="store-addr">地址: {{ store.store_addr }}</div>
                </div>
              </el-checkbox>
            </div>
          </el-checkbox-group>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { selectApi as selectAccountApi } from "@/services/systemPage/admin_user.js";
import { selectApi as selectStoreApi } from "@/services/storeConfig/storeInfo.js";
import {
  selectApi as selectMappingApi,
  addApi as addMappingApi,
  deleteApi as deleteMappingApi
} from "@/services/storeConfig/storeAccountMapping.js";

export default {
  name: "StoreAccountMapping",
  data() {
    return {
      accountList: [],
      storeList: [],
      selectedAccountId: null,
      selectedAccountName: "",
      selectedStoreIds: [],
      originalStoreIds: [] // 用于记录原始已分配的门店
    };
  },
  mounted() {
    this.loadAccountList();
    this.loadStoreList();
  },
  methods: {
    // 加载账户列表
    async loadAccountList() {
      try {
        const res = await selectAccountApi({});
        if (res.code === 200) {
          this.accountList = res.data || [];
          // 如果账户列表大于0，默认选中第一个账户
          if (this.accountList.length > 0) {
            this.handleSelectAccount(this.accountList[0]);
          }
        }
      } catch (error) {
        this.$message.error("加载账户列表失败");
      }
    },

    // 加载门店列表
    async loadStoreList() {
      try {
        const res = await selectStoreApi({});
        if (res.code === 200) {
          this.storeList = res.data || [];
        }
      } catch (error) {
        this.$message.error("加载门店列表失败");
      }
    },

    // 选择账户
    async handleSelectAccount(account) {
      this.selectedAccountId = account.uuid;
      this.selectedAccountName = account.name;

      // 加载该账户已分配的门店
      await this.loadAccountStores(account.uuid);
    },

    // 加载账户已分配的门店
    async loadAccountStores(accountId) {
      try {
        const res = await selectMappingApi({ admin_user_uuid: accountId });
        if (res.code === 200) {
          // 获取已分配的门店ID列表
          this.selectedStoreIds = (res.data || []).map(item => item.store_id);
          this.originalStoreIds = [...this.selectedStoreIds];
        }
      } catch (error) {
        this.$message.error("加载门店分配信息失败");
      }
    },

    // 全选
    handleSelectAll() {
      this.selectedStoreIds = this.storeList.map(store => store.store_id);
    },

    // 取消全选
    handleCancelAll() {
      this.selectedStoreIds = [];
    },

    // 保存分配
    async handleSaveMapping() {
      if (!this.selectedAccountId) {
        this.$message.warning("请先选择账户");
        return;
      }

      try {
        // 找出新增的门店（在selectedStoreIds中但不在originalStoreIds中）
        const addedStores = this.selectedStoreIds.filter(
          id => !this.originalStoreIds.includes(id)
        );

        // 找出删除的门店（在originalStoreIds中但不在selectedStoreIds中）
        const removedStores = this.originalStoreIds.filter(
          id => !this.selectedStoreIds.includes(id)
        );

        // 批量新增
        if (addedStores.length > 0) {
          const addPromises = addedStores.map(storeId =>
            addMappingApi({
              admin_user_uuid: this.selectedAccountId,
              store_id: storeId
            })
          );
          await Promise.all(addPromises);
        }

        // 批量删除
        if (removedStores.length > 0) {
          const deletePromises = removedStores.map(storeId =>
            deleteMappingApi({
              admin_user_uuid: this.selectedAccountId,
              store_id: storeId
            })
          );
          await Promise.all(deletePromises);
        }

        this.$message.success("保存成功");
        // 重新加载当前账户的门店分配
        await this.loadAccountStores(this.selectedAccountId);
      } catch (error) {
        this.$message.error("保存失败");
        console.error(error);
      }
    }
  }
};
</script>

<style scoped lang="less">
.account-mapping-container {
  height: 100%;
}

.content-wrapper {
  display: flex;
  gap: 10px;
  height: 100%;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e6e6e6;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }
}

.account-section {
  width: 350px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.account-list {
  flex: 1;
  overflow-y: auto;
}

.account-item {
  border: 2px solid #e6e6e6;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.3s;
  background: #fff;
  color: #313333;

  &:hover {
    background: #fffaed;
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
  }

  &.active {
    border-color: #65d27d;
    background: #fffaed;
    color: #17a236;
  }

  .account-info {
    .account-name {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 8px;
    }

    .account-detail {
      font-size: 13px;
      margin-bottom: 4px;
    }
  }
}

.store-section {
  flex: 1;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.empty-tip {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #999;
  font-size: 16px;

  i {
    font-size: 48px;
    margin-bottom: 10px;
  }

  p {
    margin: 0;
  }
}

.store-list {
  flex: 1;
  overflow-y: auto;
}

.store-item {
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 10px;
  transition: all 0.3s;

  &:hover {
    background: #f5f7fa;
    border-color: #65d27d;
  }

  .el-checkbox {
    width: 100%;
  }

  .store-content {
    padding-left: 10px;

    .store-name {
      font-size: 15px;
      font-weight: 600;
      color: #333;
      margin-bottom: 6px;
    }

    .store-info {
      font-size: 13px;
      color: #666;
      margin-bottom: 4px;

      span {
        margin-right: 15px;
      }
    }

    .store-addr {
      font-size: 12px;
      color: #999;
    }
  }
}
</style>
